import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SdBallotService } from '../../shared/services/sd-ballot.service';
import { filter, Observable, of, Subscription, switchMap, take } from 'rxjs';
import { ISdBallotDetailDto } from '../../shared/models/sd/sd-ballot-detail';
import { IBallotInternalHistory } from '../../shared/models/ballot-internal-history/ballot-internal-history';
import { ViewVoteIconsConstants } from '../../../../shared/constants/view-vote-icons-constants';
import { UnifiedBallotStatus } from '../../../../shared/enums/unified-ballot-status-enum';
import { IApiResponse } from '../../../../shared/models/api-response';
import { IUser } from '../../../../shared/models/user';
import { AuthService } from '../../../../shared/services/auth.service';
import { UserUtility } from '../../../../shared/utilities/user-utility';


@Component({
    selector: 'app-sd-details',
    templateUrl: './sd-details.component.html',
    styleUrl: './sd-details.component.scss',
    standalone: false
})
export class SdDetailsComponent implements OnInit, OnDestroy {
  loadingBallotDetail: boolean = false;
  currentUser: IUser | null = null;
  sdBallotDetail: ISdBallotDetailDto | null = null;
  selectedTab: string = '';
  initialTabId: string = 'ballotSummary';
  subscriptions: Subscription = new Subscription();
  ballotInternalHistories: IBallotInternalHistory[] = [];
  isInternalUser: boolean = false;
  visibleTabs = [
    { id: 'ballotSummary', label: 'Ballot Summary' },
    { id: 'viewVotes', label: 'View Votes' }
  ];

  public readonly ViewVoteIconsConstants = ViewVoteIconsConstants;

  constructor(
    private route: ActivatedRoute,
    private _sdBallotService: SdBallotService,
    public _authService: AuthService
  ) {
    this.initUser();
  }

  initUser() {
    const currentUserSubscription = this._authService.currentUserSubject
      .pipe(
        filter((user) => !!user),
        take(1)
      )
      .subscribe((user) => {
        this.currentUser = user;
        this.isInternalUser = UserUtility.isInternalUser(user!);
        this.updateVisibleTabs();
      });
    this.subscriptions.add(currentUserSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.route.params.subscribe((params) => {
        this.selectedTab = this.initialTabId;
      })
    );

    this.setupGetBallotDetailsSubscription();
  }

  setupGetBallotDetailsSubscription(): void {
    let obs$ = this.route.params.pipe(
      switchMap((params) => {
        const id = params['id'];

        if (!id) {
          this.loadingBallotDetail = false;
          return of(null);
        }

        return this._sdBallotService.getBallotDetails(id);
      })
    );

    if (obs$ !== undefined && obs$ !== null) {
      this.subscribeToGetBallotDetails(
        obs$ as Observable<IApiResponse<ISdBallotDetailDto>>
      );
    }
  }

  subscribeToGetBallotDetails(
    obs$: Observable<IApiResponse<ISdBallotDetailDto>> | null
  ): void {
    if (obs$ === null || obs$ === undefined) {
      return;
    }

    this.loadingBallotDetail = true;
    this.sdBallotDetail = null;

    this.subscriptions.add(
      obs$.subscribe({
        next: (response) => {
          this.loadingBallotDetail = false;

          if (!response || !response.success) {
            console.log(`Error getting ballot details: ${response?.message}`);
            return;
          }

          if (response.success) {
            this.sdBallotDetail = response.content;
            this.ballotInternalHistories = this.generateBallotInternalHistory(
              this.sdBallotDetail
            );
            this.updateVisibleTabs();
          }
        },
        error: (error) => {
          this.loadingBallotDetail = false;
          console.log(`Error getting ballot details: ${error.message}`);
        },
      })
    );
  }

  generateBallotInternalHistory(
    detail: ISdBallotDetailDto | null
  ): IBallotInternalHistory[] {
    if (!detail) {
      return [];
    }

    const name =
        detail.finalisedByName?.trim() ||
        detail.finalisedByEmail?.trim() ||
        '-';

    return [
      {
        name,
        status: 'Finalised',
        date: detail.finalisedTime ? new Date(detail.finalisedTime) : null,
        comments: detail.ballotResolution || '-',
      },
    ];
  }

  private updateVisibleTabs(): void {
    const baseTabs = [
      { id: 'ballotSummary', label: 'Ballot Summary' },
      { id: 'viewVotes', label: 'View Votes' }
    ];

    const isFinalised =
      this.sdBallotDetail?.unifiedBallotStatusId === UnifiedBallotStatus.finalised;

    this.visibleTabs =
      this.isInternalUser && isFinalised
        ? [...baseTabs, { id: 'internalHistory', label: 'Internal History' }]
        : baseTabs;
  }

  onTabChange(tabId: string) {
    this.selectedTab = tabId;
  }

  onAfterSaveVote(ballotId: string) {
    this.refreshBallotDetails(ballotId);
  }

  onFinalisedBallot(ballotId: number) {
    this.refreshBallotDetails(ballotId.toString());
  }

  refreshBallotDetails(ballotId: string) {
    if (!isNaN(ballotId as any)) {
      const ballotIdNum = parseInt(ballotId);
      let obs$ = this._sdBallotService.getBallotDetails(ballotIdNum);

      if (obs$ !== undefined && obs$ !== null) {
        this.subscribeToGetBallotDetails(
          obs$ as Observable<IApiResponse<ISdBallotDetailDto>>
        );
      }
    }
  }
}
